/**
 * Keep every cook note in the recipe's journal, not only on the planned meal (#795).
 *
 * A note written about a cooked meal ("plus de sauce", "le poulet n'était pas
 * assez cuit") is stored on that `PlannedMeal` as `cookNote`. The planned meal
 * is the plan, and the plan is rewritten all the time: Régénérer, Remplacer,
 * Retirer, a drag onto a full cell. Each of those took the note with it. On
 * 25 September one tap on Régénérer replaced Thursday's dinner, 43 minutes
 * after the household had written about it; the verdicts survived (they live
 * on the recipe) and the note did not, and there was no copy anywhere.
 *
 * So the journal (`cookLog`, one document per recipe) holds a durable copy of
 * every note, as an entry `mirrorEntryId(meal.id)` carrying `mealId`. The copy
 * is written whenever the note is, from whichever device sees the cooked meal
 * — the id and every field are derived from the meal, so two devices write the
 * same entry, not two. While the meal exists it is the source: re-saving the
 * note updates the copy, clearing it or un-marking the cook removes the copy.
 * Once the meal is gone, the copy is what is left, and that is the point.
 *
 * **Absence never deletes.** A meal missing from the store may simply not be
 * loaded yet (an unhydrated week reads as an empty one), so only a meal that is
 * present and says "not cooked" or "no note" can retract its copy.
 */
import { createListenerMiddleware, type AnyAction } from '@reduxjs/toolkit';
import type { PlannedMeal } from './mealPlanSlice';
import { isRecipeMeal } from './mealEntry';
import {
  upsertCookLogEntry,
  removeCookLogEntry,
  hydrateCookLogFromCloud,
  mirrorEntryId,
  type CookLogEntry,
} from './cookLogSlice';

export interface JournalChanges {
  upserts: CookLogEntry[];
  removals: Array<{ recipeId: string; entryId: string }>;
}

const noteOf = (meal: PlannedMeal): string => (meal.cookNote ?? '').trim();

const carriesNote = (meal: PlannedMeal): boolean =>
  meal.status === 'cooked' && !!meal.cookedAt && noteOf(meal) !== '' && isRecipeMeal(meal);

/** The copy a cooked meal's note should have in the journal. */
export const journalEntryForMeal = (meal: PlannedMeal): CookLogEntry => {
  const entry: CookLogEntry = {
    id: mirrorEntryId(meal.id),
    recipeId: meal.recipeId,
    date: meal.cookedAt!,
    rating: 0,
    note: noteOf(meal),
    mealId: meal.id,
    // Derived, never `now`: every device must produce the identical entry.
    createdAt: meal.cookedAt!,
  };
  if (meal.cookNoteBy) entry.noteBy = meal.cookNoteBy;
  return entry;
};

const sameCopy = (a: CookLogEntry, b: CookLogEntry): boolean =>
  a.recipeId === b.recipeId &&
  a.date === b.date &&
  a.note === b.note &&
  (a.noteBy ?? '') === (b.noteBy ?? '') &&
  a.mealId === b.mealId;

/**
 * What the journal must change to hold a copy of every note in the plan.
 * Pure: the listener below and the tests both call it.
 */
export const planJournalChanges = (
  weeks: Record<string, PlannedMeal[]> | undefined,
  entries: Record<string, CookLogEntry[]>,
): JournalChanges => {
  const meals = new Map<string, PlannedMeal>();
  for (const list of Object.values(weeks ?? {})) {
    for (const meal of list ?? []) if (meal?.id) meals.set(meal.id, meal);
  }

  const upserts: CookLogEntry[] = [];
  const removals: JournalChanges['removals'] = [];

  // Only the copy filed under the meal's CURRENT recipe answers to the meal. A
  // copy under another recipe was written while the meal was that dish; the
  // meal has since been pointed elsewhere, which for that recipe is the same as
  // the meal being gone, so the copy is kept.
  const copyOf = (meal: PlannedMeal): CookLogEntry | undefined =>
    entries[meal.recipeId]?.find(e => e.id === mirrorEntryId(meal.id));

  for (const meal of meals.values()) {
    const existing = copyOf(meal);
    if (carriesNote(meal)) {
      const wanted = journalEntryForMeal(meal);
      if (!existing || !sameCopy(existing, wanted)) upserts.push(wanted);
    } else if (existing) {
      // The meal is here, still this recipe, and says there is nothing to keep:
      // un-marked as cooked, or the note was cleared.
      removals.push({ recipeId: existing.recipeId, entryId: existing.id });
    }
  }

  return { upserts, removals };
};

export interface CookJournalSummary {
  timesCooked: number;
  lastCooked?: string;
  notes: Array<{ date: string; text: string; by?: string }>;
}

/**
 * One recipe's cook history, from the plan and the journal together.
 *
 * A journal entry whose meal is still cooked in the plan IS that cook — counted
 * once, its note shown once. When the meal has gone, the entry stands alone.
 */
export const summarizeCookJournal = (
  mealHistory: Array<{ mealId?: string; cookedAt: string; cookNote?: string; cookNoteBy?: string }>,
  cookLog: CookLogEntry[],
): CookJournalSummary => {
  const cookedMealIds = new Set(mealHistory.map(h => h.mealId).filter(Boolean) as string[]);
  const standalone = cookLog.filter(c => !c.mealId || !cookedMealIds.has(c.mealId));

  const dates = [
    ...mealHistory.map(h => h.cookedAt),
    ...standalone.map(c => c.date),
  ].sort((a, b) => b.localeCompare(a));

  const notes = [
    ...mealHistory
      .filter(h => !!h.cookNote?.trim())
      .map(h => ({ date: h.cookedAt, text: h.cookNote!, by: h.cookNoteBy })),
    ...standalone
      .filter(c => !!c.note?.trim())
      .map(c => ({ date: c.date, text: c.note!, by: c.noteBy })),
  ].sort((a, b) => b.date.localeCompare(a.date));

  return {
    timesCooked: mealHistory.length + standalone.length,
    lastCooked: dates[0],
    notes,
  };
};

// ── the listener ────────────────────────────────────────────────────────────

export const cookNoteJournalMiddleware = createListenerMiddleware();

interface JournalState {
  mealPlan?: { weeks?: Record<string, PlannedMeal[]> };
  cookLog: { entries: Record<string, CookLogEntry[]> };
}

// The last inputs seen, so the hundreds of mealPlan actions that change no
// note cost one reference comparison each.
let lastWeeks: unknown = null;
let lastEntries: unknown = null;

/** Test seam. */
export const resetCookNoteJournal = (): void => {
  lastWeeks = null;
  lastEntries = null;
};

const PERSIST_REHYDRATE = 'persist/REHYDRATE';

export const shouldReconcileJournal = (action: AnyAction): boolean =>
  typeof action.type === 'string' &&
  (action.type.startsWith('mealPlan/') ||
    action.type === hydrateCookLogFromCloud.type ||
    action.type === PERSIST_REHYDRATE);

cookNoteJournalMiddleware.startListening({
  predicate: (action) => shouldReconcileJournal(action as AnyAction),
  effect: (_action, api) => {
    const state = api.getState() as JournalState;
    const weeks = state.mealPlan?.weeks;
    const entries = state.cookLog?.entries ?? {};
    if (weeks === lastWeeks && entries === lastEntries) return;

    const { upserts, removals } = planJournalChanges(weeks, entries);
    for (const r of removals) api.dispatch(removeCookLogEntry(r));
    for (const e of upserts) api.dispatch(upsertCookLogEntry(e));

    const after = api.getState() as JournalState;
    lastWeeks = after.mealPlan?.weeks;
    lastEntries = after.cookLog?.entries;
  },
});

export default cookNoteJournalMiddleware;

/**
 * What the cook-capture sheet hands `markMealAsCooked` as the note. `undefined`
 * keeps the meal's note as it is; a string replaces it, `''` included.
 *
 * The sheet opens on the meal's existing note (`initialNote`), so emptying the
 * field is a deliberate clear, not "nothing typed". Before, the sheet opened
 * empty on a noted meal and a second note silently replaced the first.
 */
export function captureNoteToSave(typed: string, initialNote?: string): string | undefined {
  const trimmed = typed.trim();
  if (trimmed) return trimmed;
  return initialNote?.trim() ? '' : undefined;
}
